import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { Milestone, MilestoneType } from '@/types';
import { apiCall } from '@/utils/api';

interface MilestonesState {
  milestones: Milestone[];
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  error: string | null;
  filters: {
    epicId?: string;
    type?: MilestoneType;
  };
}

const initialState: MilestonesState = {
  milestones: [],
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  error: null,
  filters: {},
};

// Async thunks
export const fetchMilestones = createAsyncThunk(
  'milestones/fetchAll',
  async (filters?: { epicId?: string; type?: MilestoneType }) => {
    const params = new URLSearchParams();
    if (filters?.epicId) params.append('epicId', filters.epicId);
    if (filters?.type) params.append('type', filters.type);

    const response = await apiCall('GET', `/milestones${params.toString() ? `?${params}` : ''}`);
    return response.data as { milestones: Milestone[] };
  }
);

export const fetchMilestoneById = createAsyncThunk(
  'milestones/fetchById',
  async (id: string) => {
    const response = await apiCall('GET', `/milestones/${id}`);
    return response.data as { milestone: Milestone };
  }
);

export const fetchEpicMilestones = createAsyncThunk(
  'milestones/fetchByEpic',
  async (epicId: string) => {
    const response = await apiCall('GET', `/epics/${epicId}/milestones`);
    return response.data as { milestones: Milestone[] };
  }
);

export const createMilestone = createAsyncThunk(
  'milestones/create',
  async (data: {
    epicId: string;
    targetDate: string;
    completionPercentage?: number;
    description?: string;
    type: MilestoneType;
  }) => {
    const response = await apiCall('POST', '/milestones', data);
    return response.data as { milestone: Milestone };
  }
);

export const updateMilestone = createAsyncThunk(
  'milestones/update',
  async ({ id, data }: { id: string; data: Partial<Milestone> }) => {
    const response = await apiCall('PUT', `/milestones/${id}`, data);
    return response.data as { milestone: Milestone };
  }
);

export const deleteMilestone = createAsyncThunk(
  'milestones/delete',
  async (id: string) => {
    await apiCall('DELETE', `/milestones/${id}`);
    return id;
  }
);

const milestonesSlice = createSlice({
  name: 'milestones',
  initialState,
  reducers: {
    setFilters(state, action: PayloadAction<MilestonesState['filters']>) {
      state.filters = action.payload;
    },
    clearError(state) {
      state.error = null;
    },
    clearMilestones(state) {
      state.milestones = [];
    },
  },
  extraReducers: (builder) => {
    // Fetch all milestones
    builder
      .addCase(fetchMilestones.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMilestones.fulfilled, (state, action) => {
        state.isLoading = false;
        state.milestones = action.payload.milestones;
      })
      .addCase(fetchMilestones.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch milestones';
      });

    // Fetch milestone by ID
    builder
      .addCase(fetchMilestoneById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMilestoneById.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.milestones.findIndex(m => m.id === action.payload.milestone.id);
        if (index !== -1) {
          state.milestones[index] = action.payload.milestone;
        } else {
          state.milestones.push(action.payload.milestone);
        }
      })
      .addCase(fetchMilestoneById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch milestone';
      });

    // Fetch epic milestones
    builder
      .addCase(fetchEpicMilestones.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchEpicMilestones.fulfilled, (state, action) => {
        state.isLoading = false;
        state.milestones = action.payload.milestones;
      })
      .addCase(fetchEpicMilestones.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch epic milestones';
      });

    // Create milestone
    builder
      .addCase(createMilestone.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createMilestone.fulfilled, (state, action) => {
        state.isCreating = false;
        state.milestones.push(action.payload.milestone);
      })
      .addCase(createMilestone.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.error.message || 'Failed to create milestone';
      });

    // Update milestone
    builder
      .addCase(updateMilestone.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateMilestone.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.milestones.findIndex(m => m.id === action.payload.milestone.id);
        if (index !== -1) {
          state.milestones[index] = action.payload.milestone;
        }
      })
      .addCase(updateMilestone.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.error.message || 'Failed to update milestone';
      });

    // Delete milestone
    builder
      .addCase(deleteMilestone.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteMilestone.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.milestones = state.milestones.filter(m => m.id !== action.payload);
      })
      .addCase(deleteMilestone.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.error.message || 'Failed to delete milestone';
      });
  },
});

export const { setFilters, clearError, clearMilestones } = milestonesSlice.actions;

export default milestonesSlice.reducer;
